import { APIResponse } from "../types/APIResponseType";
import { AdGroupCreationBody, AdGroupTemplateCreationBody } from "../types/adGroupTypes";
import { AdvertiserType } from "../types/advertiserTypes";
import { AgencyType } from "../types/agencyTypes";
import {
  ArchiveCampaignBody,
  BrandgenCampaignCreateType,
  StatusCampaignBody,
  UpdatedCampaignBody,
} from "../types/campaignTypes";
import { CreativeIdsToDelete } from "../types/creativeTypes";
import { configuredFetch } from "./configuredFetch";
import { errorToaster } from "../helpers/toasters";

const apiRequestObject = (
  method: "get" | "put" | "post" | "delete",
  form?: boolean,
  body?: BodyInit,
): RequestInit | null => {
  // if it's put post or delete require a body
  if ((method === "put" || method === "post" || method === "delete") && !body) {
    return null;
  }

  if (method === "get") {
    return {
      credentials: "same-origin", // include, same-origin, *omit
      headers: {
        accept: "application/json",
        "Content-Type": "application/json",
      },
      method, // *GET, POST, PUT, DELETE, etc.
      mode: "cors", // no-cors, cors, *same-origin
    };
  }

  if (form) {
    return {
      body,
      credentials: "same-origin", // include, same-origin, *omit
      headers: {
        accept: "application/json",
      },
      method, // *GET, POST, PUT, DELETE, etc.
      mode: "cors", // no-cors, cors, *same-origin
    };
  }
  return {
    body,
    credentials: "same-origin", // include, same-origin, *omit
    headers: {
      accept: "application/json",
      "Content-Type": "application/json",
    },
    method, // *GET, POST, PUT, DELETE, etc.
    mode: "cors", // no-cors, cors, *same-origin
  };
};

const constructFetchCall = async (
  url: string,
  method: "get" | "put" | "post" | "delete",
  requestBody?: Object | FormData,
  form?: boolean,
  isPublicRoute?: boolean,
): Promise<APIResponse> => {
  const requestObject =
    requestBody instanceof FormData
      ? apiRequestObject(method, form, requestBody)
      : apiRequestObject(method, form, JSON.stringify(requestBody));

  if (!requestObject) {
    return { success: false, error: "There was an error with this request, request body not provided" };
  }

  try {
    const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${isPublicRoute ? "/public" : "/api"}${url}`;
    const { response } = await configuredFetch(fullUrl, requestObject);
    const parsedData = await response.json();
    return parsedData;
  } catch (error) {
    const err = error as any;
    if (err?.body?.error) {
      errorToaster(err?.body?.error, 5500);
    }
    return { success: false, error: "There was an error with this request, request body not provided" };
  }
};

/**
 * Keys in the api object are grouped together by their functionality
 */
const api = {
  // Get billing logs for advertiser by brandgenAdvertiserId
  async getBillingLogForAdvertiser(brandgenAdvertiserId: string) {
    const getBillingLogForAdvertiser = await constructFetchCall(
      `/advertiser/billinglog/${brandgenAdvertiserId}`,
      "get",
    );
    return getBillingLogForAdvertiser;
  },
  // Get user invoices
  async getInvoices(username: string) {
    const { payload: invoices } = await constructFetchCall(`/mailSents`, "post", { username });
    return JSON.parse(invoices);
  },
  // Get agency for current, logged in user, based on userId
  async getAgencyByUserName(userName: string) {
    const agency = await constructFetchCall(`/agency/agencyassignedtouser/${userName}`, "get");
    return agency;
  },
  // Create agency
  async createBrandgenAgency(agency: Partial<AgencyType>) {
    const createdAgency = await constructFetchCall(`/agency/create`, "post", agency, false, true);
    return createdAgency;
  },
  // get all brandgen billing logs, hacky work around
  async getAllBrandgenBillingLogs(username: string) {
    try {
      const { payload: allBillingLogs } = await constructFetchCall("/billingLogs/all", "post", { username });
      return JSON.parse(allBillingLogs);
    } catch (error) {
      throw new Error(`Error in getAllBrandgenBillingLogs: ${error}`);
    }
  },
  // get all brandgen billing logs, hacky work around
  async getUserBrandgenBillingLogs(username: string) {
    try {
      const { payload: userBillingLogs } = await constructFetchCall("/billingLogs/byusername", "post", { username });
      return JSON.parse(userBillingLogs);
    } catch (error) {
      throw new Error(`Error in getAllBrandgenBillingLogs: ${error}`);
    }
  },
  // get brandgen segments logs
  async getSegmentsLogs(username: string) {
    try {
      const { payload: segmentsLogs } = await constructFetchCall("/segmentsLogs/all", "post", { username });
      return JSON.parse(segmentsLogs);
    } catch (error) {
      throw new Error(`Error in getBrandgenSegmentsLogs: ${error}`);
    }
  },
  // Create advertiser
  async createAdvertiser(body: AdvertiserType) {
    const getOrCreateAdvertiser = await constructFetchCall("/advertiser/create", "post", body);
    return getOrCreateAdvertiser;
  },
  /* Gets an advertiser by BrandGen Advertiser ID */
  async getAdvertiserById(advertiserId: string) {
    const getAdvertiserById = await constructFetchCall(`/advertiser/${advertiserId}`, "get");
    return getAdvertiserById;
  },
  /* Gets all advertisers by agency id */
  async fetchAdvertisersByAgency(agencyId: string) {
    const advertisers = await constructFetchCall(`/advertiser/agency/${agencyId}`, "get");
    return advertisers;
  },
  // Create a campaign
  async getOrCreateCampaign(body: BrandgenCampaignCreateType) {
    const getOrCreateCampaign = await constructFetchCall("/campaign/create", "post", body);
    return getOrCreateCampaign;
  },
  async getCampaignById(brandgenCampaignId: string) {
    const campaign = await constructFetchCall(`/campaign/${brandgenCampaignId}`, "get");
    return campaign;
  },
  // Create an event/pixel associated with campaignId that is passed in
  async createCampaignEvent(brandgenCampaignId: string, beeswaxAdvertiserId: number, campaignName: string) {
    const updatedCampaign = await constructFetchCall("/campaign/event", "post", {
      brandgenCampaignId,
      beeswaxAdvertiserId,
      campaignName,
    });
    return updatedCampaign;
  },
  // Get options allowed for Geo Targeting
  async getGeoTargetOptions() {
    const getGeoTargetOptions = await constructFetchCall("/geo/options", "get");
    return getGeoTargetOptions;
  },
  // Get options allowed for advertiser categories - these come from BWX and we do not use them anywhere else in app
  async getAdvertiserCategories() {
    const categories = await constructFetchCall(`/advertiser/categories`, "get");
    return categories;
  },
  // Get all campaigns associate with specific advertiser
  async getAllCampaignsByAdvertiser(advertiserId: string, timeFrame: string) {
    const getAllCampaignsByAdvertiser = await constructFetchCall(
      `/campaign/allbyadvertiser/${advertiserId}?timeFrame=${timeFrame}`,
      "get",
    );
    return getAllCampaignsByAdvertiser;
  },
  async getCampaignDailyReport(bgCampaignd: string, date: Date, beeswaxCampaign: number) {
    const campaignWithReport = await constructFetchCall(
      `/campaign/dailyreport/${bgCampaignd}?date=${date}&camapign_id=${beeswaxCampaign}`,
      "get",
    );
    return campaignWithReport;
  },
  // Get stripe intent
  async stripeIntent() {
    const stripeIntent = await constructFetchCall(`/stripe/intent`, "get");
    return stripeIntent;
  },
  // Create an entry in stripe for advertiser card and associate with advertiser
  async advertiserStripeSave(body: {
    bgAdvertiserId: string;
    method?: string;
    friendlyName?: string;
    bgAgencyId?: string;
    billingEmail?: string;
  }) {
    const stripeSave = await constructFetchCall(`/stripe/advertiser/card`, "post", body);
    return stripeSave;
  },
  // Generate authorization URL used for hubspot OAuth process, which includes advertiser ID
  async getHubspotAuth(bgAdvertiserId: string) {
    const hubspotAuth = await constructFetchCall(`/hubspot/auth/${bgAdvertiserId}`, "get");
    return hubspotAuth;
  },
  // Generate HubSpot API tokens and associate with relevant advertiser
  async createHubspotTokens(code: string, bgAdvertiserId: string) {
    const advertiserWithTokens = constructFetchCall(`/hubspot/oauthtokens`, "post", { code, bgAdvertiserId });
    return advertiserWithTokens;
  },
  // Delete HubSpot API tokens with associated advertiser
  async deleteHubspotTokens(bgAdvertiserId: string) {
    const updatedAdvertiser = constructFetchCall(`/hubspot/oauthtokens/delete`, "post", { bgAdvertiserId });
    return updatedAdvertiser;
  },
  // Get tiers/sort values for companies from selected advertisers hubspot account
  async getSortValues(bgAdvertiserId: string) {
    const getSortValues = await constructFetchCall(`/hubspot/companies/properties/${bgAdvertiserId}`, "get");
    return getSortValues;
  },
  // Get lists from selected advertisers hubspot account
  async getHubSpotLists(bgAdvertiserId: string) {
    const lists = await constructFetchCall(`/hubspot/lists/${bgAdvertiserId}`, "get");
    return lists;
  },
  // Get pages from selected advertisers hubspot account, based on the types that they've selected
  async getHubspotPages(bgAdvertiserId: string, types: Array<string>) {
    const getHubspotPages = await constructFetchCall(
      `/hubspot/pages/${bgAdvertiserId}?types=${types.join(",")}`,
      "get",
    );
    return getHubspotPages;
  },
  // Get complete list of Role/Title Cookies
  async getRoleTitleList() {
    const getRoleTitleList = await constructFetchCall("/roleTitle/getRoleTitleList", "get");
    return getRoleTitleList;
  },
  async getRoleTitlesForTemplate(id: string) {
    const roleTitles = await constructFetchCall(`/roleTitle/campaignTemplate/${id}`, "get");
    return roleTitles.payload;
  },
  // Create a creative asset in BWX and BrandGen
  async createCreative(form: FormData) {
    const createCreative = await constructFetchCall("/creatives/create", "post", form, true);
    return createCreative;
  },
  async changeCreative(form: FormData) {
    const addCreative = await constructFetchCall("/creatives/change", "post", form, true);
    return addCreative;
  },
  // Update a creatives asset in BWX and BrandGen
  async updateCreativesLandingPage(adGroupId: string, landingPage: string) {
    const updatedCreative = await constructFetchCall("/creatives/updateLandingPage", "put", { adGroupId, landingPage });
    return updatedCreative;
  },
  // Update a creatives asset in BWX and BrandGen
  async deleteCreative(creativeIdsToDelete: CreativeIdsToDelete) {
    const updatedCreative = await constructFetchCall("/creatives/delete", "delete", creativeIdsToDelete);
    return updatedCreative;
  },
  // Create creative template
  async createCreativeTemplate(form: FormData) {
    const createCreativeTemplate = await constructFetchCall("/creatives/templates/create", "post", form, true);
    return createCreativeTemplate;
  },
  // Upload creative on Beeswax
  async uploadCreativeOnBeeswax(form: FormData) {
    const uploadedCreative = await constructFetchCall("/creatives/upload", "post", form, true);
    return uploadedCreative;
  },
  // Create a brandgen Ad Group for the selected advertiser
  async createAdGroup(body: AdGroupCreationBody) {
    const createAdGroup = await constructFetchCall("/adgroups/create", "post", body);
    return createAdGroup;
  },
  // Create Ad Group template
  async createAdGroupTemplate(body: AdGroupTemplateCreationBody) {
    const createAdGroup = await constructFetchCall("/adgroups/templates/create", "post", body);
    return createAdGroup;
  },
  // Get all creatives associated with the selected Ad Group
  async getCreativesByAdGroup(adGroupId: string) {
    const getCreativesByAdGroup = await constructFetchCall(`/creatives/creativesbyadgroupid/${adGroupId}`, "get");
    return getCreativesByAdGroup;
  },
  // Get ad group by id
  async getAdGroupById(id: string) {
    const getAdGroup = await constructFetchCall(`/adGroups/${id}`, "get");
    return getAdGroup.payload;
  },
  // Get ad groups by campaign id
  async getAdGroupByCampaignId(brandgenCampaignId: string) {
    const getAdGroupByCampaign = await constructFetchCall(
      `/adGroups/adgroupbybrandgencampaignid/${brandgenCampaignId}`,
      "get",
    );
    return getAdGroupByCampaign;
  },
  async deleteAdGroup(adGroupId: string) {
    const deleteAdGroup = await constructFetchCall(`/adGroups/delete`, "delete", { adGroupId });
    return deleteAdGroup;
  },
  async getCreativesTemplatesByAdGroup(adGroupTemplateId: string) {
    const getCreativesTemplatesByAdGroup = await constructFetchCall(
      `/creatives/templates/creativesbyadgroupid/${adGroupTemplateId}`,
      "get",
    );
    return getCreativesTemplatesByAdGroup;
  },
  // Get all ad groups associated with the selected advertiser
  async getAdGroupTemplatesByAdvertiser(bgAdvertiserId: string) {
    const getAdGroupsByAdvertiser = await constructFetchCall(`/adgroups/templates/${bgAdvertiserId}`, "get");
    return getAdGroupsByAdvertiser;
  },
  async createDomainList(bgAdvertiserId: string, listName: string, domains: Array<string>) {
    const domainList = await constructFetchCall(`/domainlist/create`, "post", { bgAdvertiserId, listName, domains });

    return domainList;
  },
  async getDomainLists(bgAdvertiserId: string) {
    const domainLists = await constructFetchCall(`/domainlist/advertiser/${bgAdvertiserId}`, "get");

    return domainLists;
  },
  async getPublicPublicationLists() {
    const publicationLists = await constructFetchCall(`/domainlist/public`, "get");

    return publicationLists;
  },
  // Get line item reports associated with selected campaign, and matching provided timeFrame
  async getLineItemReports(brandgenCampaignId: string, timeFrame: string) {
    const reports = await constructFetchCall(
      `/report/lineitems?brandgenCampaignId=${brandgenCampaignId}&timeFrame=${timeFrame}`,
      "get",
    );
    return reports;
  },
  async getLineItemDailyReport(brandgenCampaignId: string, date: Date) {
    const reports = await constructFetchCall(
      `/report/lineitems/dailyreport?brandgenCampaignId=${brandgenCampaignId}&date=${date}`,
      "get",
    );
    return reports;
  },
  // Get creative reports associated with selected adGroup, and matching provided timeFrame
  async getAdGroupWithCreativeReports(adGroupId: string, timeFrame: string) {
    const reports = await constructFetchCall(`/report/creatives?adGroupId=${adGroupId}&timeFrame=${timeFrame}`, "get");
    return reports;
  },
  async getAdGroupWithDailyCreativeReports(adGroupId: string, date: Date) {
    const reports = await constructFetchCall(
      `/report/creatives/dailyreport?adGroupId=${adGroupId}&date=${date}`,
      "get",
    );
    return reports;
  },
  // Get targeting selections by campaign id
  async getTargetingSelections(brandgenCampaignId: string) {
    const targetingSelections = await constructFetchCall(`/campaign/targetingSelections/${brandgenCampaignId}`, "get");
    return targetingSelections;
  },
  // Update Campaign Values
  async updateCampaign(
    brandgenCampaignId: string,
    beeswaxCampaignId: number,
    updatedCampaignBody: Partial<UpdatedCampaignBody>,
  ) {
    const payload = {
      ...updatedCampaignBody,
      daily_budget: updatedCampaignBody.dailyBudget,
      campaign_budget: updatedCampaignBody.campaign_budget,
      max_bid: updatedCampaignBody.maxBid,
      start_date: updatedCampaignBody.startDate,
      end_date: updatedCampaignBody.endDate,
    };

    const newCampaign = await constructFetchCall(`/campaign/new/details`, "put", {
      brandgenCampaignId,
      beeswaxCampaignId,
      updateBody: payload,
    });
    return newCampaign;
  },
  // Archive Campaign Values
  async archiveCampaign(brandgenCampaignId: string, updatedBody: ArchiveCampaignBody) {
    const newCampaign = await constructFetchCall(`/campaign/archive`, "put", {
      brandgenCampaignId,
      updateBody: updatedBody,
    });
    return newCampaign;
  },
  // Update Campaign Status
  async updateCampaignStatus(brandgenCampaignId: string, beeswaxCampaignId: number, updateBody: StatusCampaignBody) {
    const newCampaign = await constructFetchCall(`/campaign/new/status`, "put", {
      brandgenCampaignId,
      beeswaxCampaignId,
      updateBody,
    });
    return newCampaign;
  },
  // Update Campaign Values
  async getCSVKickfireMatches(csvCompanies: string[]) {
    const companiesMatches = await constructFetchCall(`/campaign/csv-kickfire-matches`, "post", {
      csvCompanies,
    });
    return companiesMatches;
  },
  // Get HubSpot Campaigns
  async getHubspotCampaigns(portalId: string) {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/public/crmcard/campaigns?portalId=${portalId}`);
    return response.json();
  },
  // Add company to campaign
  async addCompanyToCampaign(portalId: string, campaignId: string, domain: string, companyName: string) {
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/public/crmcard/add-company-to-campaign?portalId=${portalId}&campaignId=${campaignId}&domain=${domain}&companyName=${companyName}`,
    );
    return response.json();
  },
  // Update Line item Status
  async updateLineItemStatus(id: number, status: boolean) {
    await constructFetchCall(
      "/crmcard/line-item-status",
      "put",
      { beeswaxLineItemId: id, active: status },
      false,
      true,
    );
  },
  async createSegment(name: string, description: string, advertiserId: string) {
    await constructFetchCall("/crmcard/segments", "post", { name, description, advertiserId }, false, true);
  },
  async getSegments(advertiserId: string) {
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/public/crmcard/segments?advertiserId=${advertiserId}`,
    );

    return response.json();
  },
  async getHubspotLists(bgAdvertiserId: string) {
    const lists = await constructFetchCall(`/hubspot/lists/${bgAdvertiserId}`, "get");

    return lists;
  },
  async getCampaignTemplatesByUser(createdBy: string) {
    const templates = await constructFetchCall(`/campaign/templates/${createdBy}`, "get");
    return templates.payload;
  },
  async createCampaignTemplate(body: any) {
    const templates = await constructFetchCall(`/campaign/templates`, "post", body);
    return templates.payload;
  },
  async updateCampaignTemplate(id: string, body: any) {
    const templates = await constructFetchCall(`/campaign/templates/${id}`, "put", body);
    return templates.payload;
  },
  async deleteCampaignTemplate(id: string) {
    const response = await constructFetchCall(`/campaign/templates`, "delete", { id });
    return response;
  },
  async getPlayBooks() {
    const playBooks = await constructFetchCall("/playBooks", "get");
    return playBooks;
  },
};
export default api;
